import { dayjs } from '@frontend/duck-tape';
import type { TrackingReduxState } from '@frontend/redux/slices/trackingSlice';
import * as TrackingSlice from '@frontend/redux/slices/trackingSlice';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useSelector } from './useSelector';

export const useTrackingSlice = <TRootState extends { tracking: TrackingReduxState }>() => {
  const tracking = useSelector<TRootState>()(({ tracking }) => tracking);
  const dispatch = useDispatch();

  const setFbclid = useCallback((fbclid: string) => dispatch(TrackingSlice.setFbclid(fbclid)), [dispatch]);
  const setGclid = useCallback((gclid: string) => dispatch(TrackingSlice.setGclid(gclid)), [dispatch]);
  const setIrClickId = useCallback(
    (irClickId: string) => dispatch(TrackingSlice.setIrClickId(irClickId)),
    [dispatch],
  );
  const setRdtClickId = useCallback(
    (rdtClickId: string) => dispatch(TrackingSlice.setRdtClickId(rdtClickId)),
    [dispatch],
  );
  const setTwClickId = useCallback((twClickId: string) => dispatch(TrackingSlice.setTwClickId(twClickId)), [dispatch]);
  const setImRef = useCallback((imRef: string) => dispatch(TrackingSlice.setImRef(imRef)), [dispatch]);
  const clearTracking = useCallback(() => dispatch(TrackingSlice.clearTracking()), [dispatch]);

  const getValidFbclid = () => {
    if (!tracking || !tracking.fbclid || !tracking.fbclidTimestamp) return undefined;
    const isExpired = dayjs().diff(dayjs(tracking.fbclidTimestamp), 'day') >= 30;

    if (isExpired) {
      dispatch(TrackingSlice.clearExpiredFbclid());
      return undefined;
    }

    return tracking.fbclid;
  };

  const getValidGclid = () => {
    if (!tracking || !tracking.gclid || !tracking.gclidTimestamp) return undefined;
    const isExpired = dayjs().diff(dayjs(tracking.gclidTimestamp), 'day') >= 90;

    if (isExpired) {
      dispatch(TrackingSlice.clearExpiredGclid());
      return undefined;
    }

    return tracking.gclid;
  };

  const getValidImRef = () => {
    if (!tracking || !tracking.imRef || !tracking.imRefTimestamp) return undefined;
    const isExpired = dayjs().diff(dayjs(tracking.imRefTimestamp), 'day') >= 30;

    if (isExpired) {
      dispatch(TrackingSlice.clearExpiredImRef());
      return undefined;
    }

    return tracking.imRef;
  };

  const getValidIrClickId = () => {
    if (!tracking || !tracking.irClickId || !tracking.irClickIdTimestamp) return undefined;
    const isExpired = dayjs().diff(dayjs(tracking.irClickIdTimestamp), 'day') >= 30;

    if (isExpired) {
      dispatch(TrackingSlice.clearExpiredIrClickId());
      return undefined;
    }

    return tracking.irClickId;
  };

  const getValidRdtClickId = () => {
    if (!tracking || !tracking.rdtClickId || !tracking.rdtClickIdTimestamp) return undefined;
    const isExpired = dayjs().diff(dayjs(tracking.rdtClickIdTimestamp), 'day') >= 30;

    if (isExpired) {
      dispatch(TrackingSlice.clearExpiredRdtClickId());
      return undefined;
    }

    return tracking.rdtClickId;
  };

  const getValidTwClickId = () => {
    if (!tracking || !tracking.twClickId || !tracking.twClickIdTimestamp) return undefined;
    const isExpired = dayjs().diff(dayjs(tracking.twClickIdTimestamp), 'day') >= 30;

    if (isExpired) {
      dispatch(TrackingSlice.clearExpiredTwClickId());
      return undefined;
    }

    return tracking.twClickId;
  };

  return {
    clearTracking,
    fbclid: getValidFbclid(),
    gclid: getValidGclid(),
    imRef: getValidImRef(),
    irClickId: getValidIrClickId(),
    rdtClickId: getValidRdtClickId(),
    setFbclid,
    setGclid,
    setImRef,
    setIrClickId,
    setRdtClickId,
    setTwClickId,
    twClickId: getValidTwClickId(),
  };
};
